/**
 * StaticLonePair - Motion-enabled lone pair component with selection feedback
 *
 * Subscribes to atom MotionValues and updates lone pair position in real-time.
 * Lone pairs follow their atoms instantly (no spring animation).
 * Dynamically repositions to avoid bond intersections during dragging.
 * Integrates with FeedbackManager for hover/selection visual feedback.
 */

import { memo, useEffect } from 'react';
import { motion, useTransform } from 'motion/react';
import { useMotion } from '../../contexts/MotionContext';
import { useMolecularSVG } from '../../contexts/MolecularSVGContext';
import { getBondsForAtom } from '../../utils/dragConstraints';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { useSelection } from '../../selection';
import { useFeedback } from '../../feedback';
import { lonePairs2d } from '@alchemiesolns/layout-2d';
import { toAtomContainer } from '@alchemiesolns/workspace-serialization';
import { svgPaperAtoms } from '../../rendering/svgPaperAtoms';
import type { ElectronData } from '../../rendering/useSVGGeneration';
import type { ChargeData } from '../../rendering/useSVGGeneration';
import type { FeedbackStyle } from '../../feedback';

export interface StaticLonePairProps {
  electronData: ElectronData;
  allElectronData?: ElectronData[]; // All electron data for this atom
  allChargeData?: ChargeData[]; // All charge data to avoid charge positions
  feedbackStyle?: FeedbackStyle; // Optional feedback styling from FeedbackManager
}

/**
 * Renders a lone pair or radical that follows its atom using MotionValues
 */
export const StaticLonePair = memo(function StaticLonePair({ electronData, allElectronData }: StaticLonePairProps) {
  const { getAtomMotion } = useMotion();
  const { workspace } = useMolecularSVG();
  const format = useFormatOverride();

  // Generate unique ID for this lone pair/radical
  // Find the index of this electron among all electrons for this atom
  const electronIndex = allElectronData
    ? allElectronData.filter(e => e.atomId === electronData.atomId).indexOf(electronData)
    : 0;
  const lonePairId = `${electronData.atomId}-lp${electronIndex}`;

  // Selection and feedback integration
  const selection = useSelection({ optional: true });
  const isSelected = selection?.isSelected(lonePairId) ?? false;
  const isHovered = selection?.isHovered(lonePairId) ?? false;
  const feedback = useFeedback(lonePairId);

  // Sync feedback with selection state
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback, lonePairId]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback, lonePairId]);

  const atomMotion = getAtomMotion(electronData.atomId);

  if (!atomMotion) {
    return null;
  }

  const radius = format.lonePair.radius;
  const gap = format.lonePair.ratios.gap;
  const offset = electronData.offset;

  // Get bonds attached to this atom to dynamically calculate lone pair positioning
  const allBonds = getBondsForAtom(workspace, electronData.atomId);

  // Filter out bonds to hidden atoms for lone pair positioning
  // Hidden atoms should not affect lone pair arrangement
  const bonds = allBonds.filter(bond => {
    const neighbor = workspace.getAtom(bond.neighborId);
    if (!neighbor) return false;
    const visibility = workspace.getVisibility(bond.neighborId);
    // VisibilityFlag: 0=Visible, 1=Hidden, 2=HiddenLP
    // Only include bonds where neighbor is NOT Hidden (VisibilityFlag.Hidden = 1)
    return visibility !== 1; // Exclude Hidden atoms
  });

  // Get electron count for this atom from workspace
  const atom = workspace.getAtom(electronData.atomId);
  const electronCount = atom ? (electronData.type === 'radical' ? atom.radicals : atom.lonePairs) : 1;

  // Calculate hydrogen direction for this atom (if it has hidden H atoms)
  // This needs to be calculated once outside the transform, not dynamically
  const atomContainer = toAtomContainer(workspace);
  const visibleAtoms = atomContainer.atoms.filter(a => {
    const vis = workspace.getVisibility(a.id!);
    return vis !== 1; // Exclude Hidden atoms (VisibilityFlag.Hidden = 1)
  });
  // Get the atom from the container (not workspace) for calcHydrogenDirection
  const containerAtom = atomContainer.atoms.find(a => a.id === electronData.atomId);
  const hydrogenDir = containerAtom ? svgPaperAtoms.calcHydrogenDirection(containerAtom, atomContainer, visibleAtoms) : undefined;

  // Dynamically calculate lone pair direction based on current bond angles
  // This ensures lone pairs avoid bond intersections even during dragging
  // Motion v12: use function form with .get() for automatic dependency tracking
  const dynamicDir = useTransform(() => {
    const atomX = atomMotion.x.get();
    const atomY = atomMotion.y.get();

    // Calculate bond directions from current positions (only visible bonds)
    // Motion v12: access neighbor positions via .get() inside the transform function
    const bondDirections: number[][] = [];
    bonds.forEach(bond => {
      const neighborMotion = getAtomMotion(bond.neighborId);
      if (neighborMotion) {
        const neighborX = neighborMotion.x.get();
        const neighborY = neighborMotion.y.get();

        const dx = neighborX - atomX;
        const dy = neighborY - atomY;
        const length = Math.sqrt(dx * dx + dy * dy);

        if (length > 0) {
          bondDirections.push([dx / length, dy / length, 0]);
        }
      }
    });

    // Add hydrogen direction if this atom displays hydrogen text
    // (i.e., has hidden hydrogen atoms)
    if (hydrogenDir) {
      bondDirections.push(hydrogenDir);
    }

    // Use arrangeLonePairs to find optimal directions that avoid bonds AND hydrogen text
    // Pass the total electron count so all electrons are arranged together
    const electronVectors = lonePairs2d.arrangeLonePairs(bondDirections, electronCount);

    if (electronVectors.length > 0) {
      // Stable 1-to-1 assignment: sort both original and output by angle, match by position
      const originalAngle = Math.atan2(electronData.dir[1], electronData.dir[0]);

      // Sort output vectors by angle
      const sortedOutputs = electronVectors
        .map((dir, index) => ({
          dir,
          angle: Math.atan2(dir[1], dir[0]),
          originalIndex: index
        }))
        .sort((a, b) => a.angle - b.angle);

      // Get all original electron angles for this atom
      // We need to find where THIS electron falls in the sorted order
      const allOriginals = allElectronData
        ? allElectronData
            .filter(e => e.atomId === electronData.atomId)
            .map(e => ({
              data: e,
              angle: Math.atan2(e.dir[1], e.dir[0])
            }))
            .sort((a, b) => a.angle - b.angle)
        : [{ data: electronData, angle: originalAngle }];

      // Find which position this electron has in the sorted original list
      const myPositionInSorted = allOriginals.findIndex(e => e.data === electronData);

      // Assign to the same position in sorted outputs (with wraparound)
      const assignedIndex = myPositionInSorted % sortedOutputs.length;
      const selectedVector = sortedOutputs[assignedIndex];

      const result = [selectedVector.dir[0] * offset, selectedVector.dir[1] * offset];
      return result;
    }

    // Fallback to original direction if calculation fails
    return [electronData.dir[0], electronData.dir[1]];
  });

  // Calculate center position of the lone pair / radical
  // IMPORTANT: All hooks must be called unconditionally at top level
  // Motion v12: use function form with .get() for automatic dependency tracking
  const centerX = useTransform(() => atomMotion.x.get() + dynamicDir.get()[0]);
  const centerY = useTransform(() => atomMotion.y.get() + dynamicDir.get()[1]);

  // Calculate perpendicular vector from dynamic direction for proper orientation
  const perpData = useTransform(() => {
    const d = dynamicDir.get();
    const dirLength = Math.sqrt(d[0] * d[0] + d[1] * d[1]);
    if (dirLength === 0) return [0, 1]; // Fallback
    const normalizedDir = [d[0] / dirLength, d[1] / dirLength];
    return [-normalizedDir[1], normalizedDir[0]]; // Perpendicular in 2D
  });

  // For lone pairs, calculate dot positions perpendicular to the direction
  // Even though radicals don't use these, hooks must be called unconditionally
  // The two dots are offset perpendicular to the direction by gap/2
  const leftX = useTransform(() => centerX.get() + perpData.get()[0] * gap / 2);
  const leftY = useTransform(() => centerY.get() + perpData.get()[1] * gap / 2);
  const rightX = useTransform(() => centerX.get() - perpData.get()[0] * gap / 2);
  const rightY = useTransform(() => centerY.get() - perpData.get()[1] * gap / 2);

  if (electronData.type === 'radical') {
    // Single dot for radical with hit box for arrow drawing
    return (
      <g id={lonePairId} className="svg-radical">
        {/* Visual dot with pointer-events disabled */}
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill={"black"}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  } else {
    // Two dots for lone pair - positioned perpendicular to direction
    return (
      <g id={lonePairId} className="svg-lonepair">
        {/* Visual dots with pointer-events disabled */}
        <motion.circle
          cx={leftX}
          cy={leftY}
          r={radius}
          fill={"black"}
          style={{ pointerEvents: 'none' }}
        />
        <motion.circle
          cx={rightX}
          cy={rightY}
          r={radius}
          fill={"black"}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  }
});
